
import React from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { FormData, DiscriminationType, DisabilityDiscriminationType, ProtectedCharacteristic, EventsAndPeople } from '@/hooks/useFormState';
import { ArrowRight, ArrowLeft, Info, FileUp, MicIcon, AlertCircle } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import VoiceRecorder from '@/components/VoiceRecorder';
import FileUploader from '@/components/FileUploader';
import EventsAndPeopleManager from '@/components/EventsAndPeopleManager';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from '@/components/ui/alert';

interface DiscriminationTypeProps {
  formData: FormData;
  updateFormData: (field: keyof FormData, value: any) => void;
  onNext: () => void;
  onPrev: () => void;
  isValid: boolean;
  deepgramApiKey: string;
  setApiKey: (key: string) => void;
  addFile: (file: any) => void;
  removeFile: (fileName: string) => void;
}

const DiscriminationTypeStep: React.FC<DiscriminationTypeProps> = ({
  formData,
  updateFormData,
  onNext,
  onPrev,
  isValid,
  deepgramApiKey,
  setApiKey,
  addFile,
  removeFile
}) => {
  const handleToggleDiscriminationType = (type: DiscriminationType) => {
    const currentTypes = [...formData.discriminationTypes];
    if (currentTypes.includes(type)) {
      updateFormData('discriminationTypes', currentTypes.filter(t => t !== type));
    } else {
      updateFormData('discriminationTypes', [...currentTypes, type]);
    }
  };

  const handleToggleDisabilityDiscriminationType = (type: DisabilityDiscriminationType) => {
    const currentTypes = [...formData.disabilityDiscriminationTypes];
    if (currentTypes.includes(type)) {
      updateFormData('disabilityDiscriminationTypes', currentTypes.filter(t => t !== type));
    } else {
      updateFormData('disabilityDiscriminationTypes', [...currentTypes, type]);
    }
  };

  const handleEventsAndPeopleChange = (
    discriminationType: string,
    updatedData: EventsAndPeople
  ) => {
    // Map discrimination type to the corresponding form field
    const fieldMap: Record<string, keyof FormData> = {
      'direct': 'directDiscriminationEventsAndPeople',
      'indirect': 'indirectDiscriminationEventsAndPeople',
      'harassment': 'harassmentEventsAndPeople',
      'victimisation': 'victimisationEventsAndPeople',
      'directDisability': 'directDisabilityEventsAndPeople',
      'discriminationArisingFromDisability': 'discriminationArisingFromDisabilityEventsAndPeople',
      'failureToMakeReasonableAdjustments': 'failureToMakeReasonableAdjustmentsEventsAndPeople'
    };

    const field = fieldMap[discriminationType];
    if (field) {
      updateFormData(field, updatedData);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid) {
      onNext();
    }
  };

  // Get human-readable names for protected characteristics
  const getCharacteristicLabel = (characteristic: ProtectedCharacteristic): string => {
    const labels: Record<ProtectedCharacteristic, string> = {
      'age': 'Age',
      'disability': 'Disability',
      'genderReassignment': 'Gender Reassignment',
      'marriageAndCivilPartnership': 'Marriage & Civil Partnership',
      'pregnancyAndMaternity': 'Pregnancy & Maternity',
      'race': 'Race',
      'religionOrBelief': 'Religion or Belief',
      'sex': 'Sex',
      'sexualOrientation': 'Sexual Orientation'
    };
    
    return labels[characteristic] || characteristic;
  };

  const showStandardTypes = !formData.protectedCharacteristics.includes('disability') || 
    (formData.protectedCharacteristics.includes('disability') && 
     formData.protectedCharacteristics.length > 1);
  
  const showDisabilityTypes = formData.protectedCharacteristics.includes('disability');

  const standardDiscriminationTypes: Array<{
    id: DiscriminationType;
    label: string;
    description: React.ReactNode;
  }> = [
    {
      id: 'direct',
      label: '1. Direct Discrimination',
      description: (
        <>
          <p>Treating someone less favourably than others because of a protected characteristic.</p>
          <p className="mt-2 text-primary">
            🟢 Example: Refusing to hire someone because they are Muslim.
          </p>
        </>
      )
    },
    {
      id: 'indirect',
      label: '2. Indirect Discrimination',
      description: (
        <>
          <p>When a policy or practice applies to everyone but puts people with a particular protected characteristic at a disadvantage — and it's not a proportionate means of achieving a legitimate aim.</p>
          <p className="mt-2 text-primary">
            🟢 Example: Requiring all employees to work full-time, which disadvantages women with childcare responsibilities.
          </p>
        </>
      )
    },
    {
      id: 'harassment',
      label: '3. Harassment',
      description: (
        <>
          <p>Unwanted conduct related to a protected characteristic that violates someone's dignity or creates a hostile, degrading, or offensive environment.</p>
          <p className="mt-2 text-primary">
            🟢 Example: Making racist jokes in the workplace.
          </p>
        </>
      )
    },
    {
      id: 'victimisation',
      label: '4. Victimisation',
      description: (
        <>
          <p>Treating someone unfairly because they made or supported a complaint about discrimination under the Equality Act.</p>
          <p className="mt-2 text-primary">
            🟢 Example: Not giving a promotion to someone because they acted as a witness in a colleague's discrimination case.
          </p>
        </>
      )
    }
  ];

  const disabilityDiscriminationTypes: Array<{
    id: DisabilityDiscriminationType;
    label: string;
    description: React.ReactNode;
  }> = [
    {
      id: 'directDisability',
      label: '1. Direct Disability Discrimination',
      description: (
        <>
          <p>Treating a disabled person less favorably than others because of their disability.</p>
          <p className="mt-2 text-primary">
            🟢 Example: Not interviewing someone for a job because they are blind, even though they have all the qualifications.
          </p>
        </>
      )
    },
    {
      id: 'discriminationArisingFromDisability',
      label: '2. Discrimination Arising from Disability',
      description: (
        <>
          <p>Treating someone unfavourably because of something connected to their disability, when the employer knew (or should have known) about the disability.</p>
          <p className="mt-2 text-primary">
            🟢 Example: Dismissing a worker for taking too much sick leave without considering that it's due to a disability.
          </p>
        </>
      )
    },
    {
      id: 'failureToMakeReasonableAdjustments',
      label: '3. Failure to Make Reasonable Adjustments',
      description: (
        <>
          <p>If a disabled person is placed at a substantial disadvantage, the employer must take reasonable steps to remove or reduce that disadvantage.</p>
          <p className="mt-2 text-primary">
            🟢 Example: Not providing screen reading software for a visually impaired employee.
          </p>
        </>
      )
    }
  ];

  // Get non-disability protected characteristics
  const nonDisabilityCharacteristics = formData.protectedCharacteristics.filter(
    char => char !== 'disability'
  );

  const handleTranscription = (field: keyof FormData, text: string) => {
    // Append the transcribed text to the existing content
    const currentText = formData[field] as string || '';
    updateFormData(field, currentText ? `${currentText}\n\n${text}` : text);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 animate-fade-in">
      <div className="space-y-2 text-center">
        <h2 className="text-2xl font-semibold tracking-tight">What type(s) of discrimination?</h2>
        <p className="text-muted-foreground">➡️ The type(s) of discrimination I have suffered is:</p>
      </div>

      {/* Summary of selected protected characteristics */}
      <div className="bg-muted p-4 rounded-lg border">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
          <div className="space-y-2">
            <h3 className="font-medium">Selected Protected Characteristics:</h3>
            <div className="flex flex-wrap gap-2">
              {formData.protectedCharacteristics.map(characteristic => (
                <Badge key={characteristic} variant={characteristic === 'disability' ? 'default' : 'outline'}>
                  {getCharacteristicLabel(characteristic)}
                </Badge>
              ))}
            </div>
            
            {showStandardTypes && showDisabilityTypes && (
              <p className="text-sm text-muted-foreground">
                <strong>Important:</strong> Please provide information for both standard discrimination types 
                (related to {nonDisabilityCharacteristics.map(getCharacteristicLabel).join(', ')}) 
                and disability discrimination types separately below.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Case Description Guidance Box */}
      <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="space-y-2">
            <h3 className="font-medium text-blue-800">Important Information for Your Case:</h3>
            <p className="text-sm text-blue-700">
              Please provide a detailed description of your experience for each selected discrimination type. 
              <strong> Be sure to include:</strong>
            </p>
            <ul className="list-disc pl-5 text-sm text-blue-700 space-y-1">
              <li><strong>Key events and their dates</strong> are essential for establishing a timeline</li>
              <li>Information about <strong>people involved</strong> and <strong>witnesses</strong> greatly strengthens your case</li>
              <li>You can <strong>record audio</strong> if you prefer speaking rather than typing</li>
              <li>The more detail you provide, the better we can assess your situation</li>
            </ul>
          </div>
        </div>
      </div>

      {/* File Upload Accordion - removed the Voice Recording Settings accordion item */}
      <Accordion type="single" collapsible>
        <AccordionItem value="file-upload">
          <AccordionTrigger className="py-2">
            <div className="flex items-center space-x-2">
              <FileUp className="h-4 w-4" />
              <span>Upload Supporting Documents</span>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <FileUploader 
              onFileUploaded={addFile}
              onFileRemoved={removeFile}
              uploadedFiles={formData.uploadedFiles}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {showStandardTypes && (
        <div className="space-y-4">
          <h3 className="font-medium text-lg">
            Standard Discrimination Types
            {showDisabilityTypes && nonDisabilityCharacteristics.length > 0 && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                - For: {nonDisabilityCharacteristics.map(getCharacteristicLabel).join(', ')}
              </span>
            )}
          </h3>
          {standardDiscriminationTypes.map((type) => (
            <div key={type.id} className="flex flex-col space-y-3">
              <div className="flex items-start space-x-3 bg-white p-3 rounded-md transition-all hover:bg-gray-50 border border-gray-200 shadow-sm">
                <Checkbox
                  id={`standard-${type.id}`}
                  checked={formData.discriminationTypes.includes(type.id)}
                  onCheckedChange={() => handleToggleDiscriminationType(type.id)}
                  className="mt-1"
                />
                <div className="space-y-1">
                  <Label 
                    htmlFor={`standard-${type.id}`} 
                    className="font-medium"
                  >
                    {type.label}
                  </Label>
                  <div className="text-sm text-foreground">
                    {type.description}
                  </div>
                </div>
              </div>
              
              {/* Conditional Sections for each type */}
              {formData.discriminationTypes.includes(type.id) && (
                <div className="ml-8 mt-2 animate-accordion-down">
                  {type.id === 'direct' && (
                    <div className="bg-[#f08d3b]/10 rounded-md p-4 border border-[#f08d3b]/20">
                      <h3 className="font-medium text-[#1f416d] mb-3">DIRECT DISCRIMINATION (if applicable)</h3>
                      
                      <Tabs defaultValue="details" className="w-full">
                        <TabsList className="grid w-full grid-cols-2">
                          <TabsTrigger value="details">Details</TabsTrigger>
                          <TabsTrigger value="events">Events & People</TabsTrigger>
                        </TabsList>
                        
                        <TabsContent value="details" className="mt-4">
                          <div className="space-y-3 text-sm">
                            <p className="text-[#1f416d] font-medium">✅ To prove direct discrimination, the claimant must show:</p>
                            <p className="ml-2"><span className="text-[#f08d3b]">🔹</span> <strong>They were treated less favorably than another person</strong><br />
                              – Comparing their treatment to someone without their protected characteristic.</p>
                            
                            <p className="ml-2"><span className="text-[#f08d3b]">🔹</span> <strong>The treatment was because of a protected characteristic</strong><br />
                              – The protected characteristic was the reason for the different treatment.</p>
                          </div>
                          
                          {/* New alert box for key events requirement */}
                          <Alert className="bg-red-50 border-red-300 mt-4 mb-2">
                            <AlertCircle className="h-5 w-5 text-red-600" />
                            <AlertDescription className="text-red-800">
                              <strong>IMPORTANT:</strong> You must add at least one key event in the "Events & People" tab before proceeding. The more events you can document, the stronger your case will be.
                            </AlertDescription>
                          </Alert>
                          
                          <div className="mt-4 space-y-2">
                            <Label htmlFor="directDiscriminationDetails" className="text-[#1f416d]">
                              ➡️ Please use the template above to describe the DIRECT discrimination you have suffered and when it occurred. Give dates 📅 (LEAVE BLANK IF NOT APPLICABLE):
                            </Label>
                            <Textarea
                              id="directDiscriminationDetails"
                              value={formData.directDiscriminationDetails || ''}
                              onChange={(e) => updateFormData('directDiscriminationDetails', e.target.value)}
                              className="min-h-[120px]"
                              placeholder="Describe your experience..."
                            />
                            
                            {/* Voice Recording for Direct Discrimination */}
                            <div className="mt-4">
                              <VoiceRecorder 
                                onTranscriptionComplete={(text) => handleTranscription('directDiscriminationDetails', text)}
                                apiKey={deepgramApiKey}
                                placeholder="Record your direct discrimination experience..."
                              />
                            </div>
                          </div>
                        </TabsContent>
                        
                        <TabsContent value="events" className="mt-4">
                          <EventsAndPeopleManager 
                            data={formData.directDiscriminationEventsAndPeople}
                            onChange={(data) => handleEventsAndPeopleChange('direct', data)}
                            deepgramApiKey={deepgramApiKey}
                          />
                        </TabsContent>
                      </Tabs>
                    </div>
                  )}
                  
                  {type.id === 'indirect' && (
                    <div className="bg-[#f08d3b]/10 rounded-md p-4 border border-[#f08d3b]/20">
                      <h3 className="font-medium text-[#1f416d] mb-3">INDIRECT DISCRIMINATION (if applicable)</h3>
                      
                      <Tabs defaultValue="details" className="w-full">
                        <TabsList className="grid w-full grid-cols-2">
                          <TabsTrigger value="details">Details</TabsTrigger>
                          <TabsTrigger value="events">Events & People</TabsTrigger>
                        </TabsList>
                        
                        <TabsContent value="details" className="mt-4">
                          <div className="space-y-3 text-sm">
                            <p className="text-[#1f416d] font-medium">✅ To prove indirect discrimination, the you must show:</p>
                            <p className="ml-2"><span className="text-[#f08d3b]">🔹</span> <strong>There was a provision, criterion, or practice (PCP)</strong><br />
                              – A rule, policy, requirement, or way of doing things applied equally to everyone.</p>
                            
                            <p className="ml-2"><span className="text-[#f08d3b]">🔹</span> <strong>The PCP puts people with a particular protected characteristic at a disadvantage</strong><br />
                              – It doesn't have to be intentional, but it creates a disadvantage for the group.</p>
                            
                            <p className="ml-2"><span className="text-[#f08d3b]">🔹</span> <strong>The claimant personally suffered that disadvantage</strong><br />
                              – Not just hypothetically — they must show it negatively affected them too.</p>
                            
                            <p className="ml-2"><span className="text-[#f08d3b]">🔹</span> <strong>The employer cannot justify it as a proportionate means of achieving a legitimate aim</strong><br />
                              – If the employer can justify it, it won't amount to unlawful discrimination.</p>
                          </div>
                          
                          {/* New alert box for key events requirement */}
                          <Alert className="bg-red-50 border-red-300 mt-4 mb-2">
                            <AlertCircle className="h-5 w-5 text-red-600" />
                            <AlertDescription className="text-red-800">
                              <strong>IMPORTANT:</strong> You must add at least one key event in the "Events & People" tab before proceeding. The more events you can document, the stronger your case will be.
                            </AlertDescription>
                          </Alert>
                          
                          <div className="mt-4 space-y-2">
                            <Label htmlFor="indirectDiscriminationDetails" className="text-[#1f416d]">
                              ➡️ Please use the template above to describe the INDIRECT discrimination you have suffered and when it occurred. Give dates 📅 (LEAVE BLANK IF NOT APPLICABLE):
                            </Label>
                            <Textarea
                              id="indirectDiscriminationDetails"
                              value={formData.indirectDiscriminationDetails || ''}
                              onChange={(e) => updateFormData('indirectDiscriminationDetails', e.target.value)}
                              className="min-h-[120px]"
                              placeholder="Describe your experience..."
                            />
                            
                            {/* Voice Recording for Indirect Discrimination */}
                            <div className="mt-4">
                              <VoiceRecorder 
                                onTranscriptionComplete={(text) => handleTranscription('indirectDiscriminationDetails', text)}
                                apiKey={deepgramApiKey}
                                placeholder="Record your indirect discrimination experience..."
                              />
                            </div>
                          </div>
                        </TabsContent>
                        
                        <TabsContent value="events" className="mt-4">
                          <EventsAndPeopleManager 
                            data={formData.indirectDiscriminationEventsAndPeople}
                            onChange={(data) => handleEventsAndPeopleChange('indirect', data)}
                            deepgramApiKey={deepgramApiKey}
                          />
                        </TabsContent>
                      </Tabs>
                    </div>
                  )}
                  
                  {type.id === 'harassment' && (
                    <div className="bg-[#f08d3b]/10 rounded-md p-4 border border-[#f08d3b]/20">
                      <h3 className="font-medium text-[#1f416d] mb-3">HARASSMENT (if applicable)</h3>
                      
                      <Tabs defaultValue="details" className="w-full">
                        <TabsList className="grid w-full grid-cols-2">
                          <TabsTrigger value="details">Details</TabsTrigger>
                          <TabsTrigger value="events">Events & People</TabsTrigger>
                        </TabsList>
                        
                        <TabsContent value="details" className="mt-4">
                          <div className="space-y-3 text-sm">
                            <p className="text-[#1f416d] font-medium">✅ To prove harassment, the claimant must show:</p>
                            <p className="ml-2"><span className="text-[#f08d3b]">🔹</span> <strong>They were subjected to unwanted conduct</strong><br />
                              – Behaviour they didn't welcome or ask for (e.g. comments, jokes, gestures, messages).</p>
                            
                            <p className="ml-2"><span className="text-[#f08d3b]">🔹</span> <strong>The conduct was related to a protected characteristic</strong><br />
                              – Such as sex, race, disability, age, sexual orientation, religion, etc.</p>
                            
                            <p className="ml-2"><span className="text-[#f08d3b]">🔹</span> <strong>The conduct had the purpose or effect of:</strong></p>
                            <p className="ml-6">- Violating their dignity, or</p>
                            <p className="ml-6">- Creating an intimidating, hostile, degrading, humiliating, or offensive environment</p>
                            
                            <p className="ml-2"><span className="text-[#f08d3b]">🔹</span> <strong>It was reasonable for them to feel that way</strong><br />
                              – The impact on the person matters more than the intention behind the behaviour.</p>
                          </div>
                          
                          {/* New alert box for key events requirement */}
                          <Alert className="bg-red-50 border-red-300 mt-4 mb-2">
                            <AlertCircle className="h-5 w-5 text-red-600" />
                            <AlertDescription className="text-red-800">
                              <strong>IMPORTANT:</strong> You must add at least one key event in the "Events & People" tab before proceeding. The more events you can document, the stronger your case will be.
                            </AlertDescription>
                          </Alert>
                          
                          <div className="mt-4 space-y-2">
                            <Label htmlFor="harassmentDetails" className="text-[#1f416d]">
                              ➡️ Please use the template above to describe the HARASSMENT you have suffered and when it occurred. Give dates 📅 (LEAVE BLANK IF NOT APPLICABLE):
                            </Label>
                            <Textarea
                              id="harassmentDetails"
                              value={formData.harassmentDetails || ''}
                              onChange={(e) => updateFormData('harassmentDetails', e.target.value)}
                              className="min-h-[120px]"
                              placeholder="Describe your experience..."
                            />
                            
                            {/* Voice Recording for Harassment */}
                            <div className="mt-4">
                              <VoiceRecorder 
                                onTranscriptionComplete={(text) => handleTranscription('harassmentDetails', text)}
                                apiKey={deepgramApiKey}
                                placeholder="Record your harassment experience..."
                              />
                            </div>
                          </div>
                        </TabsContent>
                        
                        <TabsContent value="events" className="mt-4">
                          <EventsAndPeopleManager 
                            data={formData.harassmentEventsAndPeople}
                            onChange={(data) => handleEventsAndPeopleChange('harassment', data)}
                            deepgramApiKey={deepgramApiKey}
                          />
                        </TabsContent>
                      </Tabs>
                    </div>
                  )}
                  
                  {type.id === 'victimisation' && (
                    <div className="bg-[#f08d3b]/10 rounded-md p-4 border border-[#f08d3b]/20">
                      <h3 className="font-medium text-[#1f416d] mb-3">VICTIMISATION (if applicable)</h3>
                      
                      <Tabs defaultValue="details" className="w-full">
                        <TabsList className="grid w-full grid-cols-2">
                          <TabsTrigger value="details">Details</TabsTrigger>
                          <TabsTrigger value="events">Events & People</TabsTrigger>
                        </TabsList>
                        
                        <TabsContent value="details" className="mt-4">
                          <div className="space-y-3 text-sm">
                            <p className="text-[#1f416d] font-medium">✅ To prove victimisation, the claimant must show:</p>
                            <p className="ml-2"><span className="text-[#f08d3b]">🔹</span> <strong>They did a 'protected act' (or the employer believed they did/might do one)</strong><br />
                              – Making a complaint of discrimination, supporting someone's complaint, or giving evidence related to a complaint.</p>
                            
                            <p className="ml-2"><span className="text-[#f08d3b]">🔹</span> <strong>They were treated badly as a result</strong><br />
                              – They suffered a disadvantage, damage, or harm because of the protected act.</p>
                          </div>
                          
                          {/* New alert box for key events requirement */}
                          <Alert className="bg-red-50 border-red-300 mt-4 mb-2">
                            <AlertCircle className="h-5 w-5 text-red-600" />
                            <AlertDescription className="text-red-800">
                              <strong>IMPORTANT:</strong> You must add at least one key event in the "Events & People" tab before proceeding. The more events you can document, the stronger your case will be.
                            </AlertDescription>
                          </Alert>
                          
                          <div className="mt-4 space-y-2">
                            <Label htmlFor="victimisationDetails" className="text-[#1f416d]">
                              ➡️ Please use the template above to describe the VICTIMISATION you have suffered and when it occurred. Give dates 📅 (LEAVE BLANK IF NOT APPLICABLE):
                            </Label>
                            <Textarea
                              id="victimisationDetails"
                              value={formData.victimisationDetails || ''}
                              onChange={(e) => updateFormData('victimisationDetails', e.target.value)}
                              className="min-h-[120px]"
                              placeholder="Describe your experience..."
                            />
                            
                            {/* Voice Recording for Victimisation */}
                            <div className="mt-4">
                              <VoiceRecorder 
                                onTranscriptionComplete={(text) => handleTranscription('victimisationDetails', text)}
                                apiKey={deepgramApiKey}
                                placeholder="Record your victimisation experience..."
                              />
                            </div>
                          </div>
                        </TabsContent>
                        
                        <TabsContent value="events" className="mt-4">
                          <EventsAndPeopleManager 
                            data={formData.victimisationEventsAndPeople}
                            onChange={(data) => handleEventsAndPeopleChange('victimisation', data)}
                            deepgramApiKey={deepgramApiKey}
                          />
                        </TabsContent>
                      </Tabs>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showDisabilityTypes && (
        <div className="space-y-4 mt-6">
          <div className={`${showStandardTypes ? 'bg-[#f08d3b]/20' : ''} rounded-md p-4 mb-4`}>
            <h3 className="font-medium text-lg">
              Disability Discrimination Types
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                - For: Disability
              </span>
            </h3>
            <p className="text-[#1f416d] text-sm mb-4">Please indicate which types of disability discrimination you have experienced:</p>
            
            <div className="space-y-4">
              {disabilityDiscriminationTypes.map((type) => (
                <div key={type.id} className="flex flex-col space-y-3">
                  <div className="flex items-start space-x-3 bg-white p-3 rounded-md transition-all hover:bg-gray-50 border border-gray-200 shadow-sm">
                    <Checkbox
                      id={`disability-${type.id}`}
                      checked={formData.disabilityDiscriminationTypes.includes(type.id)}
                      onCheckedChange={() => handleToggleDisabilityDiscriminationType(type.id)}
                      className="mt-1"
                    />
                    <div className="space-y-1">
                      <Label 
                        htmlFor={`disability-${type.id}`} 
                        className="font-medium"
                      >
                        {type.label}
                      </Label>
                      <div className="text-sm text-foreground">
                        {type.description}
                      </div>
                    </div>
                  </div>
                  
                  {/* Conditional Sections for each type */}
                  {formData.disabilityDiscriminationTypes.includes(type.id) && (
                    <div className="ml-8 mt-2 animate-accordion-down">
                      {type.id === 'directDisability' && (
                        <div className="bg-[#f08d3b]/10 rounded-md p-4 border border-[#f08d3b]/20">
                          <h3 className="font-medium text-[#1f416d] mb-3">DIRECT DISABILITY DISCRIMINATION</h3>
                          
                          <Tabs defaultValue="details" className="w-full">
                            <TabsList className="grid w-full grid-cols-2">
                              <TabsTrigger value="details">Details</TabsTrigger>
                              <TabsTrigger value="events">Events & People</TabsTrigger>
                            </TabsList>
                            
                            <TabsContent value="details" className="mt-4">
                              <div className="space-y-3 text-sm">
                                <p className="text-[#1f416d] font-medium">✅ To prove direct disability discrimination, you must show:</p>
                                <p className="ml-2"><span className="text-[#f08d3b]">🔹</span> <strong>You were treated less favorably than another person</strong><br />
                                  – Comparing your treatment to someone who doesn't have your disability.</p>
                                
                                <p className="ml-2"><span className="text-[#f08d3b]">🔹</span> <strong>The treatment was because of your disability</strong><br />
                                  – Your disability was the reason for the different treatment.</p>
                              </div>
                              
                              {/* New alert box for key events requirement */}
                              <Alert className="bg-red-50 border-red-300 mt-4 mb-2">
                                <AlertCircle className="h-5 w-5 text-red-600" />
                                <AlertDescription className="text-red-800">
                                  <strong>IMPORTANT:</strong> You must add at least one key event in the "Events & People" tab before proceeding. The more events you can document, the stronger your case will be.
                                </AlertDescription>
                              </Alert>
                              
                              <div className="mt-4 space-y-2">
                                <Label htmlFor="directDisabilityDiscriminationDetails" className="text-[#1f416d]">
                                  ➡️ Please use the template above to describe the DIRECT DISABILITY DISCRIMINATION you have suffered and when it occurred. Give dates 📅:
                                </Label>
                                <Textarea
                                  id="directDisabilityDiscriminationDetails"
                                  value={formData.directDisabilityDiscriminationDetails || ''}
                                  onChange={(e) => updateFormData('directDisabilityDiscriminationDetails', e.target.value)}
                                  className="min-h-[120px]"
                                  placeholder="Describe your experience..."
                                />
                                
                                {/* Voice Recording for Direct Disability Discrimination */}
                                <div className="mt-4">
                                  <VoiceRecorder 
                                    onTranscriptionComplete={(text) => handleTranscription('directDisabilityDiscriminationDetails', text)}
                                    apiKey={deepgramApiKey}
                                    placeholder="Record your direct disability discrimination experience..."
                                  />
                                </div>
                              </div>
                            </TabsContent>
                            
                            <TabsContent value="events" className="mt-4">
                              <EventsAndPeopleManager 
                                data={formData.directDisabilityEventsAndPeople}
                                onChange={(data) => handleEventsAndPeopleChange('directDisability', data)}
                                deepgramApiKey={deepgramApiKey}
                              />
                            </TabsContent>
                          </Tabs>
                        </div>
                      )}
                      
                      {type.id === 'discriminationArisingFromDisability' && (
                        <div className="bg-[#f08d3b]/10 rounded-md p-4 border border-[#f08d3b]/20">
                          <h3 className="font-medium text-[#1f416d] mb-3">DISCRIMINATION ARISING FROM DISABILITY</h3>
                          
                          <Tabs defaultValue="details" className="w-full">
                            <TabsList className="grid w-full grid-cols-2">
                              <TabsTrigger value="details">Details</TabsTrigger>
                              <TabsTrigger value="events">Events & People</TabsTrigger>
                            </TabsList>
                            
                            <TabsContent value="details" className="mt-4">
                              <div className="space-y-3 text-sm">
                                <p className="text-[#1f416d] font-medium">✅ To prove discrimination arising from disability, you must show:</p>
                                <p className="ml-2"><span className="text-[#f08d3b]">🔹</span> <strong>You were treated unfavorably</strong><br />
                                  – The treatment put you at a disadvantage.</p>
                                
                                <p className="ml-2"><span className="text-[#f08d3b]">🔹</span> <strong>The treatment was because of something arising from your disability</strong><br />
                                  – There must be a connection between your disability and the reason for the treatment.</p>
                                
                                <p className="ml-2"><span className="text-[#f08d3b]">🔹</span> <strong>The employer knew (or should have known) about your disability</strong><br />
                                  – You disclosed your disability or it was obvious to the employer.</p>
                                
                                <p className="ml-2"><span className="text-[#f08d3b]">🔹</span> <strong>The employer cannot objectively justify the treatment</strong><br />
                                  – It must be a proportionate means of achieving a legitimate aim.</p>
                              </div>
                              
                              {/* New alert box for key events requirement */}
                              <Alert className="bg-red-50 border-red-300 mt-4 mb-2">
                                <AlertCircle className="h-5 w-5 text-red-600" />
                                <AlertDescription className="text-red-800">
                                  <strong>IMPORTANT:</strong> You must add at least one key event in the "Events & People" tab before proceeding. The more events you can document, the stronger your case will be.
                                </AlertDescription>
                              </Alert>
                              
                              <div className="mt-4 space-y-2">
                                <Label htmlFor="discriminationArisingFromDisabilityDetails" className="text-[#1f416d]">
                                  ➡️ Please use the template above to describe the DISCRIMINATION ARISING FROM DISABILITY you have suffered and when it occurred. Give dates 📅:
                                </Label>
                                <Textarea
                                  id="discriminationArisingFromDisabilityDetails"
                                  value={formData.discriminationArisingFromDisabilityDetails || ''}
                                  onChange={(e) => updateFormData('discriminationArisingFromDisabilityDetails', e.target.value)}
                                  className="min-h-[120px]"
                                  placeholder="Describe your experience..."
                                />
                                
                                {/* Voice Recording for Discrimination Arising from Disability */}
                                <div className="mt-4">
                                  <VoiceRecorder 
                                    onTranscriptionComplete={(text) => handleTranscription('discriminationArisingFromDisabilityDetails', text)}
                                    apiKey={deepgramApiKey}
                                    placeholder="Record your disability-related discrimination experience..."
                                  />
                                </div>
                              </div>
                            </TabsContent>
                            
                            <TabsContent value="events" className="mt-4">
                              <EventsAndPeopleManager 
                                data={formData.discriminationArisingFromDisabilityEventsAndPeople}
                                onChange={(data) => handleEventsAndPeopleChange('discriminationArisingFromDisability', data)}
                                deepgramApiKey={deepgramApiKey}
                              />
                            </TabsContent>
                          </Tabs>
                        </div>
                      )}
                      
                      {type.id === 'failureToMakeReasonableAdjustments' && (
                        <div className="bg-[#f08d3b]/10 rounded-md p-4 border border-[#f08d3b]/20">
                          <h3 className="font-medium text-[#1f416d] mb-3">FAILURE TO MAKE REASONABLE ADJUSTMENTS</h3>
                          
                          <Tabs defaultValue="details" className="w-full">
                            <TabsList className="grid w-full grid-cols-2">
                              <TabsTrigger value="details">Details</TabsTrigger>
                              <TabsTrigger value="events">Events & People</TabsTrigger>
                            </TabsList>
                            
                            <TabsContent value="details" className="mt-4">
                              <div className="space-y-3 text-sm">
                                <p className="text-[#1f416d] font-medium">✅ To prove failure to make reasonable adjustments, you must show:</p>
                                <p className="ml-2"><span className="text-[#f08d3b]">🔹</span> <strong>The employer knew (or should have known) about your disability</strong><br />
                                  – You disclosed your disability or it was obvious to the employer.</p>
                                
                                <p className="ml-2"><span className="text-[#f08d3b]">🔹</span> <strong>There was a 'provision, criterion or practice' (PCP) or physical feature that put you at a substantial disadvantage</strong><br />
                                  – Such as a workplace policy, requirement, or physical barrier.</p>
                                
                                <p className="ml-2"><span className="text-[#f08d3b]">🔹</span> <strong>A reasonable adjustment could have been made to remove or reduce that disadvantage</strong><br />
                                  – What adjustment would have been practical and effective.</p>
                                
                                <p className="ml-2"><span className="text-[#f08d3b]">🔹</span> <strong>The employer failed to make that adjustment</strong><br />
                                  – They either refused or failed to consider it.</p>
                              </div>
                              
                              {/* New alert box for key events requirement */}
                              <Alert className="bg-red-50 border-red-300 mt-4 mb-2">
                                <AlertCircle className="h-5 w-5 text-red-600" />
                                <AlertDescription className="text-red-800">
                                  <strong>IMPORTANT:</strong> You must add at least one key event in the "Events & People" tab before proceeding. The more events you can document, the stronger your case will be.
                                </AlertDescription>
                              </Alert>
                              
                              <div className="mt-4 space-y-2">
                                <Label htmlFor="failureToMakeReasonableAdjustmentsDetails" className="text-[#1f416d]">
                                  ➡️ Please use the template above to describe the FAILURE TO MAKE REASONABLE ADJUSTMENTS you have experienced and when it occurred. Give dates 📅:
                                </Label>
                                <Textarea
                                  id="failureToMakeReasonableAdjustmentsDetails"
                                  value={formData.failureToMakeReasonableAdjustmentsDetails || ''}
                                  onChange={(e) => updateFormData('failureToMakeReasonableAdjustmentsDetails', e.target.value)}
                                  className="min-h-[120px]"
                                  placeholder="Describe your experience..."
                                />
                                
                                {/* Voice Recording for Failure to Make Reasonable Adjustments */}
                                <div className="mt-4">
                                  <VoiceRecorder 
                                    onTranscriptionComplete={(text) => handleTranscription('failureToMakeReasonableAdjustmentsDetails', text)}
                                    apiKey={deepgramApiKey}
                                    placeholder="Record your experience with failure to make reasonable adjustments..."
                                  />
                                </div>
                              </div>
                            </TabsContent>
                            
                            <TabsContent value="events" className="mt-4">
                              <EventsAndPeopleManager 
                                data={formData.failureToMakeReasonableAdjustmentsEventsAndPeople}
                                onChange={(data) => handleEventsAndPeopleChange('failureToMakeReasonableAdjustments', data)}
                                deepgramApiKey={deepgramApiKey}
                              />
                            </TabsContent>
                          </Tabs>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Navigation buttons */}
      <div className="flex justify-between pt-6">
        <Button 
          type="button" 
          variant="outline" 
          onClick={onPrev}
          className="gap-1"
        >
          <ArrowLeft className="h-4 w-4" />
          Previous
        </Button>
        
        <Button 
          type="submit" 
          disabled={!isValid}
          className="gap-1"
        >
          Next
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </form>
  );
};

export default DiscriminationTypeStep;
